import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ChevronDown, BarChart3, Clock, CheckCircle2, Briefcase, Users, Award } from 'lucide-react';
import MobileHighlight from '@/components/sections/highlights/MobileHighlight';
import HeroSection from '@/components/sections/collab/HeroSection';
import ProjectTrackingSection from '@/components/sections/collab/ProjectTrackingSection';
import DirectCommunicationSection from '@/components/sections/collab/DirectCommunicationSection';
import DownloadSection from '@/components/sections/collab/DownloadSection';
import PerformanceMonitoringSection from '@/components/sections/collab/PerformanceMonitoringSection';
import SEO from '@/components/SEO';
import FAQ from '@/components/ui/FAQ';

const Collab = () => {
  const faqItems = [
    {
      question: "Comment fonctionne la collaboration avec Orcsys ?",
      answer: "Notre approche collaborative est basée sur une communication transparente et continue. Nous utilisons des outils modernes de gestion de projet et organisons des réunions régulières pour suivre l'avancement. Notre application Collab facilite cette collaboration en centralisant toutes les interactions."
    },
    {
      question: "Quelles sont les principales fonctionnalités de l'application Collab ?",
      answer: "Collab offre un tableau de bord en temps réel, un suivi des tâches et des projets, des outils de communication intégrés, des rapports de performance, et un système de partage de documents. L'application est conçue pour optimiser la collaboration entre notre équipe et nos clients."
    },
    {
      question: "Comment l'application Collab améliore-t-elle le suivi des projets ?",
      answer: "Collab permet de visualiser l'avancement des projets en temps réel, avec des métriques clés, des jalons, et des indicateurs de performance. Vous pouvez suivre les tâches en cours, les deadlines, et recevoir des notifications automatiques sur l'avancement de votre projet."
    },
    {
      question: "Quelle est votre approche de la gestion de projet ?",
      answer: "Notre approche est agile et flexible, soutenue par notre application Collab. Nous utilisons des sprints courts de 1-2 semaines, avec des revues de code et des démonstrations régulières. L'application facilite cette méthodologie en automatisant le suivi et les rapports."
    },
    {
      question: "Comment accéder et utiliser l'application Collab ?",
      answer: "L'application Collab est accessible via web et mobile. Après le démarrage d'un projet, vous recevez vos identifiants personnels. L'interface est intuitive et nous proposons une formation rapide pour vous familiariser avec toutes les fonctionnalités."
    },
    {
      question: "Quel support proposez-vous pour l'utilisation de Collab ?",
      answer: "Nous offrons un support complet : formation initiale, documentation détaillée, assistance technique en direct, et mises à jour régulières. Notre équipe est disponible pour répondre à vos questions et vous aider à tirer le meilleur parti de l'application."
    }
  ];
  
  // Scroll animation
  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };
  
  return (
    <>
      <SEO 
        title="Collab | Application de Collaboration pour Projets Digitaux"
        description="Découvrez notre application de collaboration qui simplifie la gestion de projets, le suivi des performances et la communication d'équipe en temps réel. Optimisez votre workflow avec des outils intuitifs et performants."
        keywords="collaboration, gestion de projet, communication d'équipe, suivi de performance, application collaborative, travail d'équipe, productivité, workflow digital, gestion agile"
        type="product"
        url="https://innov.orcsys.fr/collab"
        image="https://innov.orcsys.fr/images/collab-preview.jpg"
      />
      <div className="min-h-screen">
        <HeroSection />
        
        {/* Scroll down button */}
        <div className="flex justify-center -mt-16 relative z-10">
          <motion.button
            onClick={handleScrollDown}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ scale: 1.1 }}
          >
            <ChevronDown className="w-6 h-6 text-orcsys-500" />
          </motion.button>
        </div>
        
        {/* Feature highlights section with animated cards */}
        <section className="py-20 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              >
                Fonctionnalités Principales
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-lg text-slate-600 max-w-3xl mx-auto"
              >
                Collab intègre tous les outils dont vous avez besoin pour une collaboration fluide et efficace avec notre équipe.
              </motion.p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <MessageSquare className="w-10 h-10 text-orcsys-500" />,
                  title: "Communication directe",
                  description: "Échangez en temps réel avec l'équipe technique sans intermédiaire."
                },
                {
                  icon: <BarChart3 className="w-10 h-10 text-orcsys-500" />,
                  title: "Suivi en temps réel",
                  description: "Visualisez l'avancement de votre projet avec des métriques claires."
                },
                {
                  icon: <Clock className="w-10 h-10 text-orcsys-500" />,
                  title: "Planning et délais",
                  description: "Accédez aux échéanciers, livrables et documents du projet."
                },
                {
                  icon: <CheckCircle2 className="w-10 h-10 text-orcsys-500" />,
                  title: "Validation simplifiée",
                  description: "Approuvez les fonctionnalités directement dans l'application."
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                  className="bg-white rounded-xl p-6 shadow-md border border-slate-100 transition-all"
                >
                  <div className="bg-orcsys-50 w-20 h-20 rounded-lg flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        <ProjectTrackingSection />
        
        {/* <MobileHighlight 
          title="Interface Mobile Intuitive"
          description="L'application Collab est disponible sur iOS et Android avec une interface native qui offre la même expérience fluide que sur desktop. Restez connecté à votre projet où que vous soyez."
          imageUrl="https://images.unsplash.com/photo-1605810230434-7631ac76ec81?q=80&w=1470&auto=format&fit=crop"
          secondImageUrl="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1470&auto=format&fit=crop"
          storeRatings={{
            playStore: {
              rating: 4.8,
              reviews: 326,
              url: "#download"
            },
            appStore: {
              rating: 4.9,
              reviews: 412,
              url: "#download"
            }
          }}
          stats={{
            users: 15000,
            downloads: 30000,
            achievement: "Top 10 des applications de collaboration professionnelle en 2023"
          }}
          interestingFact="Collab a été développé après avoir recueilli les retours de plus de 200 clients sur leurs besoins en matière de collaboration. Chaque fonctionnalité répond à un besoin réel exprimé par nos utilisateurs."
        /> */}
        
        <PerformanceMonitoringSection />
        
        {/* Success metrics section */}
        <section className="py-20 bg-gradient-to-r from-orcsys-50 to-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              >
                Collab en Chiffres
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-lg text-slate-600 max-w-3xl mx-auto"
              >
                Des résultats probants qui témoignent de l'efficacité de notre application de collaboration.
              </motion.p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <Briefcase className="w-10 h-10 text-orcsys-500" />,
                  value: "94%",
                  label: "des projets livrés dans les délais"
                },
                {
                  icon: <Users className="w-10 h-10 text-orcsys-500" />,
                  value: "86%",
                  label: "de réduction des emails"
                },
                {
                  icon: <Award className="w-10 h-10 text-orcsys-500" />,
                  value: "98%",
                  label: "de satisfaction client"
                },
                {
                  icon: <Clock className="w-10 h-10 text-orcsys-500" />,
                  value: "43%",
                  label: "de temps gagné sur la coordination"
                }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 text-center"
                >
                  <div className="bg-orcsys-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-bold text-orcsys-600 mb-2">{stat.value}</div>
                  <p className="text-slate-600">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* <DirectCommunicationSection /> */}
        <DownloadSection />
        
        {/* Testimonials section */}
        {/* <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              >
                Ce que nos clients disent
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-lg text-slate-600 max-w-3xl mx-auto"
              >
                Découvrez comment Collab transforme la manière dont nos clients collaborent avec notre équipe.
              </motion.p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  quote: "Collab a transformé notre façon de travailler avec Orcsys. La communication est fluide et nous avons une visibilité totale sur l'avancement du projet.",
                  author: "Sophie Martin",
                  position: "Directrice Marketing, TechVision"
                },
                {
                  quote: "Grâce à Collab, nous avons réduit de 60% les emails et les réunions de suivi. Tout est centralisé et accessible en temps réel.",
                  author: "Thomas Dupont",
                  position: "CTO, StartupLab"
                },
                {
                  quote: "L'interface mobile de Collab me permet de suivre l'avancement du projet même en déplacement. Un vrai gain de temps et d'efficacité.",
                  author: "Marie Dubois",
                  position: "Product Owner, FinanceGrowth"
                }
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 shadow-md border border-slate-100"
                >
                  <div className="mb-4 text-orcsys-500">
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10,8.5C9.4,6.4,7.9,4.7,6,3.8V3c8.4,1.4,13.7,8.5,12,15.5c-1.1,4.5-5.4,7.5-10,7.5 c-5.5,0-10-4.5-10-10c0-8.2,8.9-14,8.9-14C6.9,2.1,7.4,2,7.6,1.8C7.8,1.5,7.7,1.2,7.5,1c-0.3-0.3-0.7-0.2-1,0c-0.1,0.1-1,0.8-1.6,1.4 C3.3,3.8,1.3,6.3,0.5,9.3c-0.5,1.8-0.6,3.7-0.1,5.5c1,4.2,5.3,8.2,11.6,8.2c5.3,0,10.1-3.5,11.4-8.5c1.4-5.2-0.7-11.8-6.9-15.3 c-0.4-0.2-0.8-0.1-1,0.3c-0.2,0.4-0.1,0.8,0.3,1c1.1,0.6,3.5,2.2,5.5,5.5c2.3,3.7,2.7,7.8,0.9,11.4" />
                    </svg>
                  </div>
                  <p className="text-slate-700 mb-6 italic">{testimonial.quote}</p>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-sm text-slate-600">{testimonial.position}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section> */}
        
        <FAQ 
          title="Collaboration avec Orcsys"
          subtitle="Découvrez comment nous travaillons ensemble pour réussir vos projets"
          items={faqItems}
          className="py-24 bg-slate-50"
        />
      </div>
    </>
  );
};

export default Collab;
